'use client';
import {
  DESKTOP_BADGE_HORIZONTAL_GAP,
  DESKTOP_BADGE_HORIZONTAL_PADDING,
  SIZING_1,
  SIZING_1_5,
  SIZING_2_5,
  SPACING_1_5,
  SPACING_2,
} from '@mnd-frontend/ui/shared/tokens';
import type { ReactNode } from 'react';
import styled, { type DefaultTheme } from 'styled-components';

export type BadgeVariant =
  | 'commercial'
  | 'informational'
  | 'success'
  | 'warning'
  | 'danger'
  | 'neutral';

export type BadgeProps = {
  text: string;
  variant?: BadgeVariant;
  icon?: ReactNode;
  onDark?: boolean;
  mini?: boolean;
};

interface VariantToColorProps {
  variant: BadgeVariant;
  onDark: boolean;
  theme: DefaultTheme;
}

export type MaterialCardStatusVariant =
  | 'archived'
  | 'draft'
  | 'published'
  | 'scheduled'
  | 'info'
  | 'active'
  | 'inactive';

export const materialStatusToBadgeVariant = (status?: MaterialCardStatusVariant): BadgeVariant => {
  switch (status) {
    case 'archived':
    case 'draft':
      return 'neutral';
    case 'active':
    case 'published':
      return 'success';
    case 'info':
      return 'informational';
    case 'inactive':
    case 'scheduled':
      return 'neutral';
    default:
      return 'informational';
  }
};

const variantToBackgroundColor = ({ variant, onDark, theme }: VariantToColorProps) => {
  switch (variant) {
    case 'commercial':
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkCommercial
        : theme.colorBackgroundBadgeRegularOnLightCommercial;
    case 'informational':
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkInformational
        : theme.colorBackgroundBadgeRegularOnLightInformational;
    case 'success':
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkSuccess
        : theme.colorBackgroundBadgeRegularOnLightSuccess;
    case 'warning':
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkWarning
        : theme.colorBackgroundBadgeRegularOnLightWarning;
    case 'danger':
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkDanger
        : theme.colorBackgroundBadgeRegularOnLightDanger;
    default:
      return onDark
        ? theme.colorBackgroundBadgeRegularOnDarkNeutral
        : theme.colorBackgroundBadgeRegularOnLightNeutral;
  }
};

const variantToMiniBackgroundColor = ({ variant, theme }: Omit<VariantToColorProps, 'onDark'>) => {
  switch (variant) {
    case 'commercial':
      return theme.colorBackgroundBadgeMiniCommercial;
    case 'informational':
      return theme.colorBackgroundBadgeMiniInformational;
    case 'success':
      return theme.colorBackgroundBadgeMiniSuccess;
    case 'warning':
      return theme.colorBackgroundBadgeMiniWarning;
    case 'danger':
      return theme.colorBackgroundBadgeMiniDanger;
    default:
      return theme.colorBackgroundBadgeMiniNeutral;
  }
};

const variantToTextColor = ({ variant, onDark, theme }: VariantToColorProps) => {
  switch (variant) {
    case 'commercial':
      return onDark ? theme.colorTextBadgeOnDarkCommercial : theme.colorTextBadgeOnLightCommercial;
    case 'informational':
      return onDark
        ? theme.colorTextBadgeOnDarkInformational
        : theme.colorTextBadgeOnLightSecondary;
    case 'success':
      return onDark ? theme.colorTextBadgeOnDarkSuccess : theme.colorTextBadgeOnLightSuccess;
    case 'warning':
      return onDark ? theme.colorTextBadgeOnDarkWarning : theme.colorTextBadgeOnLightWarning;
    case 'danger':
      return onDark ? theme.colorTextBadgeOnDarkDanger : theme.colorTextBadgeOnLightDanger;
    default:
      return onDark ? theme.colorTextBadgeOnDarkNeutral : theme.colorTextBadgeOnLightNeutral;
  }
};

const BadgeWrapper = styled.span<{
  $variant: BadgeVariant;
  $onDark: boolean;
  $mini: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => (props.$mini ? '0' : DESKTOP_BADGE_HORIZONTAL_GAP)};
  height: ${props =>
    props.$mini ? SIZING_1 : SIZING_2_5}; //temporary, sizingPillHeight not in Design tokens yet
  width: ${props => (props.$mini ? SIZING_1 : 'fit-content')};
  padding: ${props => (props.$mini ? '0' : `0 ${DESKTOP_BADGE_HORIZONTAL_PADDING}`)};
  border-radius: ${props => (props.$mini ? '50%' : props.theme.radiusBadge)};
  font-weight: 600;
  font-size: ${SPACING_1_5};
  line-height: ${SPACING_2};
  font-size: ${SIZING_1_5};
  color: ${props =>
    variantToTextColor({
      variant: props.$variant,
      onDark: props.$onDark,
      theme: props.theme,
    })};
  background-color: ${props =>
    props.$mini
      ? variantToMiniBackgroundColor({
          variant: props.$variant,
          theme: props.theme,
        })
      : variantToBackgroundColor({
          variant: props.$variant,
          onDark: props.$onDark,
          theme: props.theme,
        })};
`;

const IconWrapper = styled.div`
  display: inline-flex;
  span {
    width: ${SIZING_1_5};
    height: ${SIZING_1_5};
  }
`;

export const Badge = ({
  variant = 'neutral',
  icon,
  text,
  onDark = false,
  mini = false,
}: BadgeProps) => {
  if (mini) {
    return (
      <BadgeWrapper
        $variant={variant}
        $onDark={onDark}
        $mini={mini}
        role="status"
        data-testid={text}
      />
    );
  }

  return (
    <BadgeWrapper $variant={variant} $onDark={onDark} $mini={mini} role="status" data-testid={text}>
      <IconWrapper>{icon}</IconWrapper>
      <span>{text}</span>
    </BadgeWrapper>
  );
};
